// client/src/utils/gridTooltips.js
//
// THE DATA GRID'S HOVER TIP, IN THE APP'S OWN COLOURS.
//
// MUI's DataGrid renders every cell it has no custom renderer for as:
//
//     <div title={valueString} className={classes.content}>{valueString}</div>
//
// (x-data-grid/components/cell/GridCell.js). `title` is the NATIVE HTML
// attribute, so the tip that appears is drawn by the operating system — a pale
// yellow box on Windows, a grey one on macOS — and it cannot see the theme at
// all. A school on Dark Mode hovers a name and gets a light-grey system box;
// one on Oxford Blue gets the same light-grey system box. Nothing in
// `themes.js` can reach it, because nothing in the page is drawing it.
//
// THE FIX IS TO GIVE THE CELL A RENDERER. Reading that source again: the native
// `title` is only attached when `children == null` — that is, when no
// `renderCell` was supplied. Providing one removes the attribute AND the system
// tooltip with it, and leaves a MUI `Tooltip` in its place, which does follow
// the palette because `themes.js` already styles `MuiTooltip`.
//
// Applied to the whole column set at once rather than column by column: a grid
// has fifteen columns and the next one will have twenty, and a rule applied by
// hand fifteen times is a rule that will be applied fourteen times next year.

import React from 'react';
import { Tooltip } from '@mui/material';

/**
 * The cell, with a themed tip instead of the browser's.
 *
 * ONLY WHEN IT WOULD HELP. A tooltip repeating text that is already fully
 * visible is noise that follows the pointer around, so the tip is attached
 * only when the content is actually cut off — measured on the element, the
 * same test the grid's own column headers make.
 */
function TooltipCell({ value }) {
  const ref = React.useRef(null);
  const [clipped, setClipped] = React.useState(false);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // scrollWidth exceeds clientWidth exactly when the text is being cut.
    setClipped(el.scrollWidth > el.clientWidth + 1);
  }, [value]);

  const text = value == null ? '' : String(value);

  const content = (
    <span
      ref={ref}
      style={{
        display: 'block',
        width: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );

  // `enterDelay` so moving across a wide table does not fire a tip per column.
  return clipped
    ? <Tooltip title={text} enterDelay={400} placement="top-start">{content}</Tooltip>
    : content;
}

/**
 * Every plain column given a themed tip; anything with its own renderer, or
 * any column that draws rather than writes, left exactly as it was.
 *
 * `actions` columns hold buttons, and a checkbox column holds a checkbox —
 * wrapping either in a text tooltip would both look wrong and swallow a click.
 */
export default function withThemedTooltips(columns) {
  return (columns || []).map((col) => {
    if (!col || col.renderCell) return col;
    if (col.type === 'actions' || col.field === '__check__') return col;
    return {
      ...col,
      renderCell: (params) => (
        <TooltipCell value={params?.formattedValue ?? params?.value} />
      ),
    };
  });
}
